import React from "react";
import { useWeeklyReport } from "../hooks/useData";

const AQI_COLORS = {
  good: "#10B981", moderate: "#FBBF24",
  unhealthy_sensitive: "#F97316", unhealthy: "#F97316",
  very_unhealthy: "#EF4444", hazardous: "#FF4444",
};

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">{title}</div>
      {children}
    </div>
  );
}

function StatRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ color: "var(--subtext)", fontSize: 12 }}>{label}</span>
      <span style={{ fontFamily: "var(--mono)", fontSize: 12, fontWeight: 700, color: color || "var(--text)" }}>{value}</span>
    </div>
  );
}

export default function Reports() {
  const { data: report, isLoading } = useWeeklyReport();

  if (isLoading) {
    return (
      <div className="fade-in">
        <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 24 }}>WEEKLY REPORT</h1>
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 120, marginBottom: 16 }} />
        ))}
      </div>
    );
  }

  const summary = report?.executive_summary || {};
  const aqCategory = summary.pm25_aqi_category || "unknown";

  return (
    <div className="fade-in">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 4 }}>WEEKLY REPORT</h1>
          <p style={{ color: "var(--subtext)", fontSize: 12 }}>{report?.period}</p>
        </div>
        <div style={{
          padding: "6px 14px",
          borderRadius: 6,
          background: "rgba(0,229,176,0.1)",
          border: "1px solid rgba(0,229,176,0.3)",
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--accent)",
          cursor: "pointer",
        }}
          onClick={() => {
            const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `high-horse-report-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
          }}
        >
          ↓ EXPORT JSON
        </div>
      </div>

      <Section title="Executive Summary">
        <StatRow label="Global PM2.5 Average" value={`${summary.pm25_global_avg ?? "—"} µg/m³`} color={AQI_COLORS[aqCategory]} />
        <StatRow label="AQI Category" value={aqCategory.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} color={AQI_COLORS[aqCategory]} />
        <StatRow label="Global PM10 Average" value={`${summary.pm10_global_avg ?? "—"} µg/m³`} />
        <StatRow label="Active Fire Events" value={summary.active_fire_count ?? "—"} color="#F97316" />
        <StatRow label="Total Alerts" value={summary.total_alerts ?? "—"} />
        <StatRow label="Critical Alerts" value={summary.critical_alerts ?? "—"} color="#FF4444" />
      </Section>

      <Section title="Air Quality · Top Hotspots">
        {(report?.air_quality?.top_hotspots || []).map((h, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontSize: 12, color: "var(--subtext)" }}>
              {i + 1}. {h.city || h.location || "Unknown"}, {h.country}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: AQI_COLORS[h.aqi_category] || "var(--text)" }}>
                {h.value} µg/m³
              </span>
            </div>
          </div>
        ))}
      </Section>

      <Section title="Critical Alerts This Week">
        {(report?.alerts?.critical || []).length === 0 ? (
          <div style={{ color: "var(--success)", fontSize: 12, padding: "8px 0" }}>✓ No critical alerts this week</div>
        ) : (report?.alerts?.critical || []).map((a, i) => (
          <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontSize: 12 }}>{a.message}</div>
            <div style={{ fontSize: 10, color: "var(--subtext)", marginTop: 2 }}>{a.country} · {a.type}</div>
          </div>
        ))}
      </Section>

      <Section title="Active Fire Summary">
        <StatRow label="Total Active Fire Points" value={report?.fires?.active_count ?? "—"} color="#F97316" />
        {(report?.fires?.top_fires || []).slice(0, 5).map((f, i) => (
          <StatRow key={i} label={`${f.acq_date} · ${f.lat?.toFixed(2)}, ${f.lon?.toFixed(2)}`} value={`FRP ${f.frp} MW`} color="#F97316" />
        ))}
      </Section>
    </div>
  );
}
